import { z } from 'zod';

import { TaskRecordSchema } from '../types/task-progress.js';

// ── MIGRATE_LEGACY_TENANT_REQUESTED ──────────────────────────────────
// Event: PersonaOpportunityEvents.MIGRATE_LEGACY_TENANT_REQUESTED
// When:  a platform admin confirms "Migrate to hub" on the legacy opportunities page (AD-3547).
//
// The opportunities-engine copies the tenant's legacy competitor sets into the hub tables, reports
// progress phase by phase on the task card, then remaps the tenant's references to the hub rows.

/** Payload for `PERSONA_OPPORTUNITY.MIGRATE_LEGACY_TENANT_REQUESTED`. */
export const PersonaOpportunityMigrateLegacyTenantRequestedPayload = z.object({
  tenantId: z.string().min(1),
  requestedBy: z.string().min(1),
});
export type PersonaOpportunityMigrateLegacyTenantRequestedPayload = z.infer<
  typeof PersonaOpportunityMigrateLegacyTenantRequestedPayload
>;

export const LEGACY_HUB_MIGRATION_TASK_TYPE = 'migrate-legacy-opportunities-to-hub';
export const LEGACY_HUB_MIGRATION_STAGE_ID = 'migrate';
/** `taskLockKey` scope of the per-tenant single-run lock, which holds the running correlation id. */
export const LEGACY_HUB_MIGRATION_LOCK_SCOPE = 'legacy-hub-migration';
/** Lock and last-run markers live this long; the worker refreshes the lock while it runs. */
export const LEGACY_HUB_MIGRATION_LOCK_TTL_SECONDS = 2 * 60 * 60;
/**
 * `taskLockKey` scope of the per-run heartbeat, keyed by correlation id. The worker refreshes it after
 * every set; once it expires, the run is treated as interrupted and a redelivery resumes it.
 */
export const LEGACY_HUB_MIGRATION_HEARTBEAT_SCOPE = 'legacy-hub-migration-run';
export const LEGACY_HUB_MIGRATION_HEARTBEAT_TTL_SECONDS = 15 * 60;
/** Stable codes in a failed run's `error`; raw error text stays in the worker logs. */
export const LEGACY_HUB_MIGRATION_ERROR_CODES = {
  /** The worker stopped mid-run (crash, restart) and its heartbeat expired. */
  INTERRUPTED: 'INTERRUPTED',
  /** The run was queued but no worker picked it up within the heartbeat window. */
  NOT_STARTED: 'NOT_STARTED',
} as const;

/** Task card phases, in run order. Copy phases advance together per set; remap runs last. */
export const LEGACY_HUB_MIGRATION_PHASES = [
  { id: 'sets', label: 'Opportunity sets' },
  { id: 'opportunities', label: 'Opportunities' },
  { id: 'scripts', label: 'Scripts' },
  { id: 'scenes', label: 'Scenes' },
  { id: 'joinRows', label: 'Product links' },
  { id: 'remap', label: 'Remapping references' },
] as const;
export type LegacyHubMigrationPhaseId = (typeof LEGACY_HUB_MIGRATION_PHASES)[number]['id'];

const EntityCountsSchema = z.object({
  source: z.number().int().nonnegative(),
  wouldInsert: z.number().int().nonnegative(),
  alreadyAnchored: z.number().int().nonnegative(),
});

const RemapCountsSchema = z.object({
  source: z.number().int().nonnegative(),
  remapped: z.number().int().nonnegative(),
});

export const LegacyHubCopyCountsSchema = z.object({
  sets: EntityCountsSchema,
  products: EntityCountsSchema,
  opportunities: EntityCountsSchema,
  /** The hidden custom "Your Scripts" opportunities, kept apart so `opportunities` matches the visible counts. Defaults to 0 for runs recorded before this count existed. */
  customOpportunities: EntityCountsSchema.default({ source: 0, wouldInsert: 0, alreadyAnchored: 0 }),
  scripts: EntityCountsSchema,
  scenes: EntityCountsSchema,
  backReferencesFilled: z.number().int().nonnegative(),
  /** Hub scripts copied earlier that get their missing legacy asset fields on this run. Defaults to 0 for runs recorded before this count existed. */
  scriptAssetsFilled: z.number().int().nonnegative().default(0),
});

export const LegacyHubRemapCountsSchema = z.object({
  creativeFlags: RemapCountsSchema,
  marketplaceOrderScripts: RemapCountsSchema,
  shareLinks: RemapCountsSchema,
});

/** Dry run, and the summary of a finished run: `wouldInsert` means copied once the run has applied. */
export const LegacyHubMigrationSummarySchema = z.object({
  copy: LegacyHubCopyCountsSchema,
  remap: LegacyHubRemapCountsSchema,
});
export type LegacyHubMigrationSummary = z.infer<typeof LegacyHubMigrationSummarySchema>;

export const StartLegacyHubMigrationRequestSchema = z.object({
  idempotencyKey: z.string().min(1).max(200),
});

export const StartLegacyHubMigrationResponseSchema = z.object({
  correlationId: z.string().min(1),
  /** True when a run for this tenant was already going; its correlation id is returned instead. */
  alreadyRunning: z.boolean(),
});
export type StartLegacyHubMigrationResponse = z.infer<typeof StartLegacyHubMigrationResponseSchema>;

/** ACCEPTED is seeded by the API when it queues the run; the worker writes the rest. */
export const LegacyHubMigrationRunStatusSchema = z.enum(['ACCEPTED', 'PROCESSING', 'COMPLETED', 'FAILED']);

/** What the worker writes to the run's correlation status. */
export const LegacyHubMigrationRunResultSchema = z.object({
  status: LegacyHubMigrationRunStatusSchema,
  summary: LegacyHubMigrationSummarySchema.optional(),
  error: z.string().optional(),
});
export type LegacyHubMigrationRunResult = z.infer<typeof LegacyHubMigrationRunResultSchema>;

export const LegacyHubMigrationStatusResponseSchema = z.object({
  running: z.boolean(),
  /** The tenant's latest run, while its markers are still cached. */
  correlationId: z.string().nullable(),
  result: LegacyHubMigrationRunResultSchema.nullable(),
  /** The run's task card with per-phase progress and counts, while it is still cached. */
  task: TaskRecordSchema.nullable(),
});
export type LegacyHubMigrationStatusResponse = z.infer<typeof LegacyHubMigrationStatusResponseSchema>;
